import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import EmptyState from '@/components/ui/EmptyState'
import NotAvailable from '@/components/ui/NotAvailable'
import Pagination from '@/components/ui/Pagination'
import RefreshBar from '@/components/ui/RefreshBar'
import SectionHead from '@/components/ui/SectionHead'
import SegmentedControl, { type SegmentOption } from '@/components/ui/SegmentedControl'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import { useLastClose } from '@/features/market/lib/useLastClose'
import { pricesKeys } from '@/features/prices/api/keys'
import { GAINERS_LOSERS_WEEK_AVAILABLE } from '@/features/prices/api/pricesService'
import { useGainersLosers } from '@/features/prices/api/queries'
import GLTable from '@/features/prices/components/GLTable'
import { useManualRefresh } from '@/hooks/useManualRefresh'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import {
  gainersLosersSearchSchema,
  loadGainersLosers,
  toGainersLosersQuery,
} from '@/screens/market/GainersLosersScreen.loader'
import type { GainersLosers, GainersLosersPeriod, GainersLosersQuery } from '@/schemas/prices'
import { toPageSize } from '@/schemas/table'

const HEADER = <TitleRow title="Gainers & Losers" />

const PERIODS: readonly SegmentOption<GainersLosersPeriod>[] = [
  { value: '1d', label: '1 day' },
  { value: '1w', label: '1 week' },
]

/** A table under its own title: "Top gainers", 14/600, 8 above its panel. */
function TableBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <SectionHead title={title} size="sm" />
      {children}
    </section>
  )
}

/** The two columns, side by side from `@2xl` of the main column and 32 apart. */
const COLUMNS = 'grid gap-8 @2xl:grid-cols-2'

/** The toggle's row: 36 tall, at the right end, so the pending page holds its place. */
const TOGGLE_ROW = 'flex justify-end'

/** One TableBlock: the title line over a `pageSize`-row, two-line GLTable. */
function GainersLosersBlockSkeleton({ rows }: { rows: number }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <Skeleton className="h-4 w-28" />
      </div>
      <DataTableSkeleton rows={rows} columns={3} rowHeight="lg" />
    </div>
  )
}

function GainersLosersPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading gainers and losers…"
      header={HEADER}
      pageTitle="Gainers & Losers"
    >
      <div className="flex flex-col gap-4">
        <div className={TOGGLE_ROW}>
          <Skeleton className="h-9 w-40" />
        </div>
        <div className={COLUMNS}>
          <GainersLosersBlockSkeleton rows={10} />
          <GainersLosersBlockSkeleton rows={10} />
        </div>
      </div>
    </ScreenLayout>
  )
}

function GainersLosersError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Couldn't load gainers and losers"
      description="The data did not come back. Nothing on your device is wrong."
    />
  )
}

export const Route = createFileRoute('/_app/market/gainers-losers')({
  validateSearch: gainersLosersSearchSchema,
  loader: loadGainersLosers,
  pendingComponent: GainersLosersPending,
  errorComponent: GainersLosersError,
  component: GainersLosersScreen,
})

type Navigate = ReturnType<typeof Route.useNavigate>

/**
 * The two tables and their one pager. Its own component so the queries below only run for a
 * period mm-be serves: "1 week" has no feed yet (`GAINERS_LOSERS_WEEK_AVAILABLE`) and the screen
 * draws its no-data state instead of mounting this.
 */
function GainersLosersBoard({
  query,
  navigate,
}: {
  query: GainersLosersQuery
  navigate: Navigate
}) {
  const online = useOnline()
  const result = useGainersLosers(query)
  const manual = useManualRefresh([result])
  const lastClose = useLastClose<GainersLosers>(pricesKeys.gainersLosersTables(), result.data)
  const data = lastClose.data
  const view = lastClose.isShowingSaved ? 'loaded' : resolveViewState(result, { online })

  if (view === 'failed' || view === 'offline') {
    return (
      <EmptyState
        kind={view}
        size="page"
        title={view === 'offline' ? undefined : "Couldn't load gainers and losers"}
        onRetry={manual.refresh}
        retrying={manual.isManual}
        meta={view === 'failed' ? lastUpdatedMeta(result.dataUpdatedAt) : undefined}
        savedAt={lastClose.savedAt}
        onShowLastClose={lastClose.showLastClose}
      />
    )
  }

  return (
    <>
      {lastClose.isShowingSaved ? <RefreshBar state="offline" onRetry={manual.refresh} /> : null}
      {/* Gainers above losers until the main column has room for two: sized against the
          ScreenLayout track with a container query, not `lg` (a window breakpoint cannot see
          the rail beside it). `@2xl`, not `@lg`: each table needs ~240px for Symbol, Price and
          % chg with the figures at full width. The pager is the foot of both, so the two live
          in ONE block of the stack: `Pagination` draws its own 24px and a parent `gap-*` would
          add to it. */}
      <div>
        <div className={COLUMNS}>
          <TableBlock title="Top gainers">
            <GLTable
              rows={data?.gainers.rows ?? []}
              caption="Top gainers"
              isLoading={result.isLoading}
              pageSize={query.pageSize}
            />
          </TableBlock>
          <TableBlock title="Top losers">
            <GLTable
              rows={data?.losers.rows ?? []}
              caption="Top losers"
              isLoading={result.isLoading}
              pageSize={query.pageSize}
            />
          </TableBlock>
        </div>
        <Pagination
          page={query.page}
          pageSize={query.pageSize}
          // Each column has its own length (62 rose, 31 fell); the pager walks the longer one and
          // the shorter simply runs out of rows on the last pages.
          total={Math.max(data?.gainers.total ?? 0, data?.losers.total ?? 0)}
          unit="names"
          onPageChange={(page) => {
            void navigate({ search: (prev) => ({ ...prev, page }) })
          }}
          onPageSizeChange={(size) => {
            void navigate({
              search: (prev) => ({ ...prev, pageSize: toPageSize(size, query.pageSize), page: 1 }),
            })
          }}
        />
      </div>
    </>
  )
}

function GainersLosersScreen() {
  const query = toGainersLosersQuery(Route.useSearch())
  const navigate = Route.useNavigate()
  const weekMissing = query.period === '1w' && !GAINERS_LOSERS_WEEK_AVAILABLE

  return (
    <ScreenLayout header={HEADER} pageTitle="Gainers & Losers">
      {/* The toggle and the tables are one block, 16 apart: the pages revamp's one gap from a
          toolbar to its first block, and not the column's 24 between blocks. The toggle is the
          mock's `mka-seg`, right-aligned over the two columns; a period is a different list, so
          picking one goes back to page 1. */}
      <div className="flex flex-col gap-4">
        <div className={TOGGLE_ROW}>
          <SegmentedControl
            size="sm"
            skin="quiet"
            ariaLabel="Period"
            options={PERIODS}
            value={query.period}
            onChange={(period) => {
              void navigate({ search: (prev) => ({ ...prev, period, page: 1 }) })
            }}
          />
        </div>

        {weekMissing ? (
          <NotAvailable thing="Gainers and losers for a week" size="page" />
        ) : (
          <GainersLosersBoard query={query} navigate={navigate} />
        )}
      </div>
    </ScreenLayout>
  )
}
